import { useState, useEffect, DependencyList } from 'react';

/**
 * Generic state interface for async data loading
 */
export interface AsyncDataState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Options for useAsyncData hook
 */
export interface UseAsyncDataOptions {
  /**
   * Custom error message if the fetch fails
   */
  errorMessage?: string;
  /**
   * HMR event name to listen for (e.g., 'episodes-updated')
   */
  hmrEventName?: string;
  /**
   * Skip the initial fetch (useful when dependencies are not ready)
   */
  skip?: boolean;
}

/**
 * Generic hook for loading async data with loading and error states
 *
 * This hook eliminates code duplication across all data-fetching hooks.
 * It handles:
 * - Loading state management
 * - Error handling with custom error messages
 * - Component unmount cleanup (isMounted pattern)
 * - HMR (Hot Module Replacement) support for development
 *
 * @param fetcherFn - Async function that fetches the data
 * @param deps - Dependency array for useEffect (when to re-fetch)
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * // Load all episodes
 * const { data: episodes, loading, error } = useAsyncData(
 *   getEpisodes,
 *   [],
 *   { errorMessage: 'Failed to load episodes', hmrEventName: 'episodes-updated' }
 * );
 *
 * // Load single episode by slug
 * const { data: episode, loading, error } = useAsyncData(
 *   () => getEpisodeBySlug(slug),
 *   [slug],
 *   { errorMessage: 'Failed to load episode', hmrEventName: 'episodes-updated', skip: !slug }
 * );
 * ```
 */
export function useAsyncData<T>(
  fetcherFn: () => Promise<T>,
  deps: DependencyList = [],
  options: UseAsyncDataOptions = {}
): AsyncDataState<T> {
  const {
    errorMessage = 'Failed to load data',
    hmrEventName,
    skip = false
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Skip fetch if skip option is true
    if (skip) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const result = await fetcherFn();

        if (isMounted) {
          setData(result);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(errorMessage));
          setLoading(false);
        }
      }
    }

    loadData();

    // Listen for HMR updates (development only)
    if (import.meta.hot && hmrEventName) {
      const handleUpdate = () => {
        console.log(`[useAsyncData] ${hmrEventName} event received, reloading...`);
        loadData();
      };

      import.meta.hot.on(hmrEventName, handleUpdate);

      return () => {
        isMounted = false;
        import.meta.hot?.off(hmrEventName, handleUpdate);
      };
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip, errorMessage, hmrEventName]);

  return { data, loading, error };
}
